import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from "@mui/material";
import { ArrowBack, ContentCopy, Check } from "@mui/icons-material";
import { fetchUsageRequest } from "../../../Services/usageService";
import { C, FONT, MONO, cardSx, formatMs, formatDateTime } from "./usageTheme";
import { MethodPill, StatusPill, RolePill, EndpointText } from "./UsageBits";

function CopyButton({ text, label = "Copy" }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable (insecure context) — nothing to do
    }
  };
  return (
    <Button
      size="small"
      onClick={copy}
      startIcon={copied ? <Check sx={{ fontSize: 16 }} /> : <ContentCopy sx={{ fontSize: 15 }} />}
      sx={{ textTransform: "none", fontSize: 12.5, color: copied ? C.ok : C.textBody, borderRadius: 1.5, px: 1.25, bgcolor: C.gray7, "&:hover": { bgcolor: C.gray6 } }}
    >
      {copied ? "Copied" : label}
    </Button>
  );
}

function Field({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.textFaint, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>{label}</Typography>
      <Box sx={{ fontSize: 13.5, color: C.ink, wordBreak: "break-word" }}>{children}</Box>
    </Box>
  );
}

function CodeBlock({ value, maxHeight = 520 }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        borderRadius: "14px",
        background: "#1E2430",
        color: "#E6EAF2",
        fontFamily: MONO,
        fontSize: 12,
        lineHeight: 1.55,
        overflow: "auto",
        maxHeight,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {value}
    </Box>
  );
}

const pretty = (value) => (typeof value === "string" ? value : JSON.stringify(value, null, 2));

const isEmpty = (value) =>
  value === null || value === undefined || value === "" || (typeof value === "object" && Object.keys(value).length === 0);

// One card with a tab per payload part, instead of a grid of cards — empty
// parts become greyed-out tabs rather than blank boxes taking up space.
function PayloadTabs({ data }) {
  const tabs = [
    { id: "response", label: "Response body", value: data.response_body },
    { id: "request", label: "Request body", value: data.request_body },
    { id: "query", label: "Query", value: data.query },
    { id: "headers", label: "Headers", value: data.request_headers },
    ...(data.error_stack ? [{ id: "stack", label: "Error stack", value: data.error_stack }] : []),
  ].map((t) => ({ ...t, empty: isEmpty(t.value) }));

  const firstFilled = (data.is_error && data.error_stack ? tabs.find((t) => t.id === "stack") : null) || tabs.find((t) => !t.empty) || tabs[0];
  const [active, setActive] = React.useState(firstFilled.id);
  const current = tabs.find((t) => t.id === active) || tabs[0];
  const text = current.empty ? "" : pretty(current.value);

  return (
    <Box sx={{ ...cardSx, p: 2.5, minWidth: 0 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 1.75 }}>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", p: 0.5, borderRadius: "12px", background: C.gray7 }}>
          {tabs.map((t) => {
            const selected = t.id === active;
            return (
              <Box
                key={t.id}
                component="button"
                type="button"
                onClick={() => !t.empty && setActive(t.id)}
                disabled={t.empty}
                sx={{
                  all: "unset",
                  cursor: t.empty ? "default" : "pointer",
                  px: 1.5,
                  py: 0.625,
                  borderRadius: "9px",
                  fontSize: 12.5,
                  fontWeight: selected ? 700 : 500,
                  color: t.empty ? C.textFaint : selected ? C.ink : C.textBody,
                  background: selected ? C.surface : "transparent",
                  boxShadow: selected ? "0 1px 2px rgba(16,24,40,0.08)" : "none",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  "&:hover": !t.empty && !selected ? { color: C.ink } : {},
                }}
              >
                {t.label}
                {t.empty && <Box component="span" sx={{ fontSize: 10.5, fontWeight: 500 }}>· empty</Box>}
              </Box>
            );
          })}
        </Box>
        {!current.empty && <CopyButton text={text} />}
      </Box>
      {current.empty ? (
        <Typography sx={{ fontSize: 13, color: C.textFaint, py: 1 }}>Nothing was sent here.</Typography>
      ) : (
        <CodeBlock value={text} />
      )}
    </Box>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUsageRequest(id)
      .then((res) => !cancelled && setData(res.data))
      .catch((err) => !cancelled && setError(err.message || "Failed to load request"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [id]);

  const goBack = () => (window.history.length > 1 ? navigate(-1) : navigate("/usage?tab=requests"));

  return (
    <Box sx={{ fontFamily: FONT, maxWidth: 1200, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
        <Tooltip title="Back">
          <IconButton onClick={goBack} size="small" sx={{ bgcolor: "#FFFFFF", border: "1px solid #e0e0e0", borderRadius: 1.5 }}>
            <ArrowBack sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Typography sx={{ fontSize: 13, color: C.textSecondary }}>API Usage / Request #{id}</Typography>
      </Box>

      {loading && <CircularProgress size={22} sx={{ color: C.gray1 }} />}
      {error && <Alert severity="error">{error}</Alert>}

      {data && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Overview */}
          <Box sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap", mb: 1 }}>
              <MethodPill method={data.method} />
              <Box sx={{ fontSize: 18 }}>
                <EndpointText endpoint={data.endpoint} />
              </Box>
              <StatusPill code={data.status_code} />
            </Box>
            <Typography sx={{ fontSize: 12.5, color: C.textSecondary, fontFamily: MONO, mb: 2.5, wordBreak: "break-all" }}>{data.path}</Typography>

            {data.is_error && data.error_message && (
              <Alert severity="error" sx={{ mb: 2.5, fontSize: 13 }}>
                {data.error_message}
              </Alert>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2.5 }}>
              <Field label="Time">{formatDateTime(data.created_at)}</Field>
              <Field label="Duration">{formatMs(data.duration_ms)}</Field>
              <Field label="User">
                {data.user_email || "—"}
                {data.user_id && <Box component="span" sx={{ color: C.textFaint, ml: 0.75, fontSize: 12 }}>#{data.user_id}</Box>}
              </Field>
              <Field label="Role"><RolePill role={data.user_role} /></Field>
              <Field label="Origin">{data.origin || "—"}</Field>
              <Field label="IP address"><Box component="span" sx={{ fontFamily: MONO, fontSize: 12.5 }}>{data.ip || "—"}</Box></Field>
              <Box sx={{ gridColumn: { xs: "1 / -1", md: "span 2" } }}>
                <Field label="Browser / client"><Box component="span" sx={{ fontSize: 12.5, color: C.textBody }}>{data.user_agent || "—"}</Box></Field>
              </Box>
            </Box>
          </Box>

          {/* curl */}
          {data.curl && (
            <Box sx={{ ...cardSx, p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.75 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.ink }}>cURL</Typography>
                  <Typography sx={{ fontSize: 12, color: C.textFaint }}>Secrets are replaced with [REDACTED]</Typography>
                </Box>
                <CopyButton text={data.curl} label="Copy cURL" />
              </Box>
              <CodeBlock value={data.curl} maxHeight={220} />
            </Box>
          )}

          <PayloadTabs data={data} />
        </Box>
      )}
    </Box>
  );
}
